import React, { useCallback, useEffect, useState } from "react";
import { Button, Drawer, Form, Input, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DictService } from "../../services";
import { Dict, CreateDictDto, UpdateDictDto, DictQueryParams } from "../../types/dict";
import ErrorHandler from "../../utils/errorHandler";
import MainLayout from "../../components/layout/MainLayout";
import { ConfirmDialog, DrawerFooter, EmptyState, Icon, Pager, StatusBadge, Surface, ToolbarGroup, formatTime } from "../../components/ui";
import "./DictManagement.scss";

type Query = { dictType: string; page: number; limit: number };
type DictRow = Dict & { remark?: string };

const DictManagementPage: React.FC = () => {
  const [dicts, setDicts] = useState<DictRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState<Query>({ dictType: "", page: 1, limit: 20 });
  const [typeInput, setTypeInput] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<DictRow | null>(null);
  const [deleting, setDeleting] = useState<DictRow | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [form] = Form.useForm();

  const loadDicts = useCallback(async (q: Query) => {
    setLoading(true);
    try {
      const params: DictQueryParams = { page: q.page, limit: q.limit };
      if (q.dictType) params.dictType = q.dictType;
      const response = await DictService.getDictList(params);
      if (response.code === 0 && response.data) {
        setDicts(response.data.list || []);
        setTotal(response.data.total || 0);
      } else {
        message.error(response.msg || "获取字典列表失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDicts(query);
  }, [query, loadDicts]);

  const openDrawer = (dict?: DictRow) => {
    setEditing(dict || null);
    form.resetFields();
    if (dict) form.setFieldsValue(dict);
    setDrawerOpen(true);
  };

  const handleSave = async (values: CreateDictDto | UpdateDictDto) => {
    setSaving(true);
    try {
      const response = editing ? await DictService.updateDict(editing.id, values) : await DictService.createDict(values as CreateDictDto);
      if (response.code === 0) {
        message.success(`${editing ? "更新" : "创建"}字典成功`);
        setDrawerOpen(false);
        loadDicts(query);
      } else {
        message.error(response.msg || "操作失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      const response = await DictService.deleteDict(deleting.id);
      if (response.code === 0) {
        message.success("删除字典成功");
        setDeleting(null);
        loadDicts(query);
      } else {
        message.error(response.msg || "删除失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const applyType = (value: string) => setQuery((prev) => ({ ...prev, dictType: value.trim(), page: 1 }));

  const columns: ColumnsType<DictRow> = [
    { title: "类型", dataIndex: "dictType", key: "dictType", width: 100, render: (type: string) => <StatusBadge tone="ink" dot={false}>{type}</StatusBadge> },
    { title: "字典键", dataIndex: "dictKey", key: "dictKey", width: 210, render: (key: string) => <span className="mono dict-page__key">{key}</span> },
    { title: "字典值", dataIndex: "dictValue", key: "dictValue", render: (value: string) => <span className="mono is-wrap dict-page__value">{value}</span> },
    { title: "中文名称", dataIndex: "dictCnName", key: "dictCnName", width: 160, render: (name: string) => <strong className="dict-page__name">{name}</strong> },
    {
      title: "备注",
      key: "remark",
      width: 200,
      render: (_, record) => {
        const note = record.remark ?? record.description;
        return note ? <span className="dict-page__note">{note}</span> : <span className="text-faint">—</span>;
      },
    },
    { title: "更新时间", dataIndex: "updatedAt", key: "updatedAt", width: 156, render: (time: string, record) => <span className="mono text-muted">{formatTime(time || record.createdAt)}</span> },
    {
      title: "操作",
      key: "action",
      width: 110,
      align: "right",
      render: (_, record) => (
        <span className="ddp-actions">
          <button type="button" className="ddp-link" onClick={() => openDrawer(record)}>
            编辑
          </button>
          <button type="button" className="ddp-link ddp-link--muted" onClick={() => setDeleting(record)}>
            删除
          </button>
        </span>
      ),
    },
  ];

  return (
    <MainLayout>
      <Surface
        toolbar={
          <>
            <ToolbarGroup>
              <Input
                className="dict-page__search"
                allowClear
                prefix={<Icon name="filter" />}
                placeholder="按字典类型筛选，如 system"
                aria-label="字典类型"
                value={typeInput}
                onChange={(e) => {
                  setTypeInput(e.target.value);
                  if (!e.target.value && query.dictType) applyType("");
                }}
                onPressEnter={() => applyType(typeInput)}
              />
            </ToolbarGroup>
            <ToolbarGroup>
              <Button type="primary" icon={<Icon name="plus" />} onClick={() => openDrawer()}>
                新增字典
              </Button>
            </ToolbarGroup>
          </>
        }
        footer={<Pager total={total} current={query.page} pageSize={query.limit} onChange={(page, limit) => setQuery((prev) => ({ ...prev, page, limit }))} />}
      >
        <Table
          columns={columns}
          dataSource={dicts}
          rowKey="id"
          loading={loading}
          pagination={false}
          tableLayout="fixed"
          scroll={{ x: 1100 }}
          locale={{
            emptyText: (
              <EmptyState
                icon="book"
                title={query.dictType ? `没有「${query.dictType}」类型的字典` : "还没有字典"}
                desc={query.dictType ? "检查类型拼写，或清空筛选查看全部。" : "字典用于系统级键值配置，例如新客赠送算力。"}
                action={
                  query.dictType ? (
                    <Button
                      onClick={() => {
                        setTypeInput("");
                        applyType("");
                      }}
                    >
                      清空筛选
                    </Button>
                  ) : (
                    <Button type="primary" onClick={() => openDrawer()}>新增字典</Button>
                  )
                }
              />
            ),
          }}
        />
      </Surface>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
        rootClassName="ddp-drawer"
        destroyOnClose
        title={
          <span className="ddp-drawer-title">
            <span>{editing ? "编辑字典" : "新增字典"}</span>
            <span className="ddp-drawer-title__sub">{editing ? editing.dictKey : "修改后立即影响读取该配置的功能"}</span>
          </span>
        }
        footer={
          <DrawerFooter>
            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              {editing ? "保存修改" : "创建字典"}
            </Button>
          </DrawerFooter>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="dictType" label="字典类型" rules={[{ required: true, message: "字典类型不能为空" }]}>
            <Input placeholder="例如：system" className="mono" />
          </Form.Item>
          <Form.Item name="dictKey" label="字典键" rules={[{ required: true, message: "字典键不能为空" }]}>
            <Input placeholder="例如：new_user_token_gift" className="mono" />
          </Form.Item>
          <Form.Item name="dictValue" label="字典值" rules={[{ required: true, message: "字典值不能为空" }]}>
            <Input.TextArea placeholder="例如：500" className="mono" autoSize={{ minRows: 1, maxRows: 6 }} />
          </Form.Item>
          <Form.Item name="dictCnName" label="中文名称" rules={[{ required: true, message: "字典中文名称不能为空" }]}>
            <Input placeholder="例如：新客赠送算力" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea placeholder="请输入备注（可选）" rows={3} />
          </Form.Item>
        </Form>
      </Drawer>

      <ConfirmDialog
        open={!!deleting}
        icon="trash"
        title={`删除字典「${deleting?.dictCnName || deleting?.dictKey || ""}」？`}
        confirmText="删除"
        confirmIcon="trash"
        loading={deleteLoading}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      >
        <p>删除后，读取该配置的功能会回退到默认行为，此操作无法撤销。</p>
        {deleting && (
          <dl className="ddp-kv">
            <dt>类型</dt>
            <dd className="mono">{deleting.dictType}</dd>
            <dt>字典键</dt>
            <dd className="mono">{deleting.dictKey}</dd>
            <dt>字典值</dt>
            <dd className="mono">{deleting.dictValue}</dd>
          </dl>
        )}
      </ConfirmDialog>
    </MainLayout>
  );
};

export default DictManagementPage;
